import { Box, Grid } from '@mui/material';

import { Field, useFormikContext } from 'formik';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';

import { FormError } from 'app/components/FormError/FormError';
import { translations } from 'locales/i18n';
import { Company } from 'types/AppTypes';

interface Props {
  company: Company;
  editMode?: boolean;
}

const CompanyDescription = ({ company, editMode }: Props) => {
  const { t } = useTranslation();
  const formik = useFormikContext<Company>();

  const readOnlyFields = () => (
    <div className="section">
      <div className="section__header">{t(translations.common.description())}</div>
      <Grid container>
        <div className="rich-text">
          <Box className="ql-editor">{parse(company.description)}</Box>
        </div>
      </Grid>
    </div>
  );

  if (!editMode) {
    return readOnlyFields();
  }
  const { errors, setFieldValue } = formik;

  return (
    <div className="section">
      <div className="section__header">{t(translations.common.description())}</div>
      <Grid container>
        <Field
          name="description"
          error={errors.description}
          helperText={errors.description}
          theme="snow"
        >
          {({ field }) => (
            <ReactQuill
              className="company-detail__body__rte"
              value={field.value}
              onChange={field.onChange(field.name)}
              onBlur={(prev, source, editor) => {
                const content = editor.getHTML();
                setFieldValue('description', content.replace(/<p><br><\/p>/g, ''));
              }}
              theme="snow"
              modules={{
                toolbar: [
                  ['bold', 'italic'],
                  [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
                  ['clean']
                ],
                clipboard: {
                  allowed: {
                    tags: ['a', 'b', 'strong', 'u', 's', 'i', 'p', 'br', 'ul', 'ol', 'li', 'span'],
                    attributes: ['href', 'rel', 'target', 'class']
                  },
                  keepSelection: true,
                  substituteBlockElements: true,
                  magicPasteLinks: true,
                  matchVisual: false
                }
              }}
              formats={[
                'header',
                'bold',
                'italic',
                'align',
                'size',
                'font',
                'color',
                'underline',
                'strike',
                'blockquote',
                'list',
                'bullet',
                'indent',
                'link',
                'image'
              ]}
            />
          )}
        </Field>
        {errors.description && <FormError message={errors.description} />}
      </Grid>
    </div>
  );
};

export default CompanyDescription;
